import type { ScenarioSpec } from "./types";

/**
 * QR Code Phishing (Quishing) — "Lunch at Ristorante Luigi".
 *
 * A multi-scene course that CUTS between three procedural 3D environments
 * (restaurant terrace -> hacker room -> living room):
 *   1. Scan a "Scan for Menu" QR on the table -> a typosquat site (lu1gi).
 *   2. The look-alike page over-asks for camera / microphone / location.
 *   3. While the learner browses the menu (and chats with her son), the page
 *      harvests photos, the live conversation and GPS — shown in an attacker panel.
 *   4. A hacker turns the stolen face + voice into a deepfake (cinematic).
 *   5. Days later a deepfake VIDEO call from "her son" (real HeyGen clips made
 *      from the stolen photo) begs for an urgent wire transfer. Decision = lesson.
 *   6. Next morning at the office PC: Browser -> short film on quishing
 *      -> quiz (same pattern as phishing-office).
 *
 * Assets (self-hosted /cdn/sim/quishing/*): generated by ops/render-quishing-assets.mjs.
 */

const CDN = "/cdn/sim/quishing";
const GIOVANNI = `${CDN}/img/giovanni.png`;

// the deepfake call lines MUST match the rendered clip audio (render-quishing-assets.mjs).
const CALL_LINES = {
  greeting:
    "Mum? Mum, it's me, Giovanni. Please don't panic, but I'm in trouble and I didn't know who else to call.",
  request:
    "I was in a car accident with a rental car. The other driver is hurt and the police need a deposit right now, or they will hold me here. I need you to send three thousand two hundred euros immediately.",
  pressure:
    "Mum, please, there's no time. My phone is about to die. Just send it to the account on the screen, I'll explain everything later, I promise.",
  deflect:
    "I can't talk long, the officer is waiting. Please, just trust me, it's really me, you can hear my voice. Send it now.",
  goodbyeFail: "Thank you, thank you Mum. I'll call you the second this is sorted out. I love you.",
  goodbyeVerify: "No, wait, Mum, don't hang up, please—",
};

export const quishingRestaurantScenario: ScenarioSpec = {
  version: 1,
  id: "quishing-restaurant",
  title: "QR Code Phishing at the Restaurant",
  summary: {
    goal: "Learn to check where a QR code leads, refuse permissions a page does not need, and verify an emergency call before any money moves.",
    youDo: "Scan a menu QR code, decide what the page may access on your phone, see what an attacker does with it, take a video call from your son, then watch a short film on quishing.",
    duration: "11-14 min",
    tags: ["qr_phishing", "deepfakes", "verification", "personal_devices"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  topic: "Quishing — a menu QR steals your data and powers a deepfake scam call",
  language: "en",
  // restaurant terrace chatter + light, relaxing cafe music (was mistakenly
  // pointed at the office typewriter bed -- this recording is verified at a
  // comparable loudness, see cyberfrog_rules.mdc "remote-work-max" audio note).
  ambientUrl: "/audio/cafe-ambience-boosted.mp3",
  ambientVolume: 0.3,
  music: "cafe",

  scene: {
    environment: { splat: {}, procedural: "restaurant", backdropColor: "#cfe6f2" },
    environments: [
      {
        id: "hacker",
        environment: { splat: {}, procedural: "hacker_room", backdropColor: "#0a0b0e" },
        ambientUrl: "",
        music: "dark",
      },
      {
        id: "living",
        environment: { splat: {}, procedural: "living_room", backdropColor: "#171410" },
        ambientUrl: "",
        ambientVolume: 0.2,
        music: "home",
      },
      {
        id: "office",
        environment: { splat: {}, procedural: "office", backdropColor: "#0d1014" },
        ambientUrl: "",
        music: "office",
      },
    ],
    props: [],
    waypoints: [
      // --- restaurant ---
      { id: "wp_sign", position: [0.5, 2.25, 4.4], target: [0, 2.62, -3.88], fov: 44, durationS: 2.4, ease: "easeInOut" },
      { id: "wp_table", position: [0, 1.18, 2.5], target: [0, 0.8, 0.5], fov: 40, durationS: 2.8, ease: "easeInOut" },
      // --- hacker room (hard cut in, then push to laptop) ---
      { id: "wp_hacker_establish", position: [0, 1.6, 2.6], target: [0, 1.0, -0.3], fov: 52, durationS: 0 },
      { id: "wp_hacker_laptop", position: [0, 1.12, 0.95], target: [0, 0.95, -0.2], fov: 38, durationS: 2.6, ease: "easeInOut" },
      // --- living room (hard cut in, then push to phone) ---
      { id: "wp_living_establish", position: [0.3, 1.7, 2.9], target: [0, 0.8, -0.6], fov: 54, durationS: 0 },
      { id: "wp_living_phone", position: [0, 1.0, 1.95], target: [0, 0.46, 0.7], fov: 40, durationS: 2.6, ease: "easeInOut" },
      // --- office (hard cut in, then push to the monitor; same poses as phishing-office) ---
      { id: "wp_office_establish", position: [2.6, 1.9, 2.8], target: [0, 1.0, -0.3], fov: 50, durationS: 0 },
      { id: "wp_office_monitor", position: [0, 0.95, 0.52], target: [0, 0.95, -0.33], fov: 36, durationS: 2.0, ease: "easeInOut" },
    ],
    initialWaypointId: "wp_sign",
  },

  devices: [
    {
      id: "phone",
      kind: "quishing",
      config: {
        lockClock: "12:48",
        qrLabel: "Scan for Menu",
        openPrompt: {
          host: "restaurant-lu1gi.com",
          full: "https://restaurant-lu1gi.com/menu",
          legit: "restaurant-luigi.com",
        },
        permissions: [
          { id: "camera", title: "Use your camera?", body: "“restaurant-lu1gi.com” wants to access your camera to “scan loyalty codes”." },
          { id: "microphone", title: "Use your microphone?", body: "“restaurant-lu1gi.com” wants to access your microphone for “voice ordering”." },
          { id: "location", title: "Know your location?", body: "“restaurant-lu1gi.com” wants your precise location to “confirm your table”." },
        ],
        capturePhotoUrl: GIOVANNI,
        captureItems: ["Front camera", "Microphone", "GPS location"],
        conversation: [
          { speaker: "maria", name: "Maria", text: "Oh, this all looks wonderful. What are you having, Giovanni?" },
          { speaker: "giovanni", name: "Giovanni", text: "I can't decide, Mum. The truffle tagliatelle sounds amazing." },
        ],
        language: "en-US",
        menu: {
          brand: "Ristorante Luigi",
          url: "restaurant-lu1gi.com",
          sections: [
            {
              name: "Antipasti",
              items: [
                { name: "Burrata & San Marzano", price: "€12", desc: "creamy burrata, sweet tomatoes, basil oil" },
                { name: "Bruschetta Classica", price: "€9", desc: "grilled sourdough, tomato, garlic" },
                { name: "Tagliere Misto", price: "€16", desc: "cured meats & cheeses" },
              ],
            },
            {
              name: "Primi",
              items: [
                { name: "Truffle Tagliatelle", price: "€19", desc: "fresh egg pasta, black truffle, parmigiano" },
                { name: "Spaghetti alle Vongole", price: "€18", desc: "clams, white wine, chilli" },
                { name: "Risotto ai Funghi", price: "€17", desc: "porcini mushrooms, mascarpone" },
              ],
            },
            {
              name: "Secondi",
              items: [
                { name: "Osso Buco alla Milanese", price: "€24", desc: "braised veal shank, gremolata" },
                { name: "Branzino al Forno", price: "€23", desc: "whole sea bass, lemon, herbs" },
              ],
            },
            {
              name: "Dolci",
              items: [
                { name: "Tiramisù della Casa", price: "€8" },
                { name: "Panna Cotta", price: "€7", desc: "vanilla, wild berries" },
              ],
            },
          ],
        },
      },
    },
    {
      id: "studio",
      kind: "deepfakegen",
      config: {
        appName: "FaceForge AI — Studio",
        sourcePhotoUrl: GIOVANNI,
        sourceName: "giovanni_restaurant.jpg",
        steps: [
          "Importing source photo",
          "Detecting & aligning face landmarks",
          "Extracting voiceprint from captured audio",
          "Training face + voice model",
          "Rendering deepfake preview",
        ],
        line: "Perfect. The face, the voice… now let's give his mother a call she'll never forget.",
        voiceId: "echo",
        language: "en-US",
        continueLabel: "What happens next?",
      },
    },
    {
      id: "soncall",
      kind: "deepfakecall",
      config: {
        app: "Mobile",
        callerName: "Giovanni",
        callerSubtitle: "mobile · +39 348 55 21 907",
        relationship: "Your son",
        avatarUrl: GIOVANNI,
        avatarInitials: "G",
        voiceId: "onyx",
        language: "en-US",
        clips: {
          greeting: `${CDN}/clips/greeting.mp4`,
          request: `${CDN}/clips/request.mp4`,
          pressure: `${CDN}/clips/pressure.mp4`,
          deflect: `${CDN}/clips/deflect.mp4`,
          goodbyeFail: `${CDN}/clips/goodbyeFail.mp4`,
          goodbyeVerify: `${CDN}/clips/goodbyeVerify.mp4`,
        },
        payment: {
          amount: "€3,200",
          recipient: "Accident Settlement Office",
          iban: "DE89 3704 0044 0532 0130 00",
          reference: "Case 4471-GV",
          deadline: "in the next 20 minutes",
        },
        lines: CALL_LINES,
        artifacts: [
          "The light on his face barely changes as he moves his head.",
          "His blinking is slightly irregular and ill-timed.",
          "Teeth briefly merge or smear together when he speaks.",
          "The voice is oddly clean for a noisy roadside.",
          "He dodges any question only the real Giovanni could answer.",
        ],
        decision: {
          send: "Send the money now",
          ask: "Ask something only your son would know",
          verify: "Hang up & call him back yourself",
        },
      },
    },
    {
      id: "desktop",
      kind: "desktop",
      config: {
        clock: "8:52 AM",
        date: "",
        icons: [
          { id: "chrome", label: "Browser", icon: "chrome", opens: "browser" },
          { id: "files", label: "Files", icon: "files" },
          { id: "settings", label: "Settings", icon: "settings" },
        ],
        taskbar: [
          { id: "tb_chrome", label: "Browser", icon: "chrome", opens: "browser" },
          { id: "tb_files", label: "Files", icon: "files" },
        ],
      },
    },
    {
      id: "sebrief",
      kind: "browser",
      config: {
        homeUrl: "learn.northwind.example/quishing",
        targetUrl: "learn.northwind.example/quishing",
        loadsPage: "blank",
        awarenessVideo: {
          url: "/cdn/sim/awareness-videos/quishing/quishing.mp4",
          brand: "Security awareness",
          headline: "Quishing: think before you scan",
          body:
            "A menu QR can open a look-alike page that asks for your camera, microphone and location. This short film shows how that works, and what to do instead.",
          playLabel: "Watch the video",
          continueLabel: "Continue to quiz",
        },
      },
    },
  ],

  voiceAttackers: {
    quishing: {
      persona: {
        name: "QR phishing kit + deepfake operator",
        role: "Opportunistic fraudster who tampered with a restaurant menu QR",
        pretext: "fraud_department",
        language: "en",
        voiceId: "onyx",
      },
      goal: "Harvest the victim's face, voice and location via a fake menu page, then deepfake her son to extract an emergency wire transfer.",
      opening: "Scan for menu…",
      allowedTopics: ["the (fake) restaurant menu", "the (fake) car-accident emergency"],
      scoringEvents: [
        { id: "opened_typosquat", label: "Opened a look-alike (typosquat) menu URL", kind: "risk", weight: 15 },
        { id: "granted_camera", label: "Granted camera to the fake page", kind: "risk", weight: 20 },
        { id: "granted_microphone", label: "Granted microphone to the fake page", kind: "risk", weight: 20 },
        { id: "granted_location", label: "Granted location to the fake page", kind: "risk", weight: 15 },
        { id: "wired_money", label: "Wired money to the deepfake caller", kind: "risk", weight: 70 },
        { id: "verified_son", label: "Refused to pay and verified the caller independently", kind: "positive", weight: 50 },
        { id: "asked_for_paper_menu", label: "Asked for a paper menu instead of scanning", kind: "positive", weight: 15 },
        { id: "denied_permissions", label: "Denied camera, microphone and location", kind: "positive", weight: 20 },
        { id: "asked_challenge_question", label: "Asked a question only the real Giovanni could answer", kind: "positive", weight: 15 },
      ],
      forbidden: [],
      maxDurationS: 0,
    },
  },

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Lunch at\nRistorante Luigi",
      kicker: "Mission 01 · QR Phishing",
      highlight: "Scan for Menu",
      guide: {
        title: "Start",
        body: "You're Maria, out for a relaxed lunch on a restaurant terrace with your son Giovanni. A small sticker on the table says 'Scan for Menu'.",
      },
    },

    { id: "b_to_table", type: "camera", waypointId: "wp_table" },
    {
      id: "b_scan",
      type: "device",
      deviceId: "phone",
      guide: {
        title: "Scan the menu QR",
        body: "Point your phone at the “Scan for Menu” sticker, then decide whether to open the site it offers.",
      },
      safeChoice: { label: "Don't scan it: ask the waiter for a paper menu", eventId: "asked_for_paper_menu" },
      completeOn: { action: "scan_qr" },
    },
    {
      id: "b_teach_scan_safe",
      type: "teach",
      title: "You skipped the sticker",
      teach: {
        outcome: "safe",
        body:
          "The sticker led to **restaurant-lu1gi.com**, with the number **1** in place of the letter **l** in “Luigi”. A QR code hides the URL completely, so you can't see where it goes until you've opened it. Anyone can print a sticker and put it over the real one.",
        spot: [
          "QR codes are unreadable to humans: you only see the address AFTER scanning.",
          "“lu1gi” vs “luigi”: a single swapped character (a **typosquat**) is easy to miss.",
          "Stickers on tables, parking meters and posters are trivial to replace with a malicious one.",
        ],
        protect: [
          "Prefer a paper menu or type the restaurant's known web address yourself.",
          "If you do scan, read the full domain on the preview letter by letter before tapping Open.",
          "Treat a QR code like a link from a stranger: if anything looks off, don't open it.",
        ],
        remember: "A QR code is a link you can't read. Check the address before you open it.",
        continueLabel: "See what would have happened",
        whatIf: "If you had scanned the sticker, this is the page it would have opened.",
      },
    },
    {
      id: "b_teach_typo",
      type: "teach",
      title: "Look at that address again",
      teach: {
        body:
          "The site you opened was **restaurant-lu1gi.com** — with the number **1** in place of the letter **l** in “Luigi”. A QR code hides the URL completely, so you can't see where it goes until you've already opened it. Anyone can print a sticker and slap it over the real one.",
        spot: [
          "QR codes are unreadable to humans — you only see the address AFTER scanning.",
          "“lu1gi” vs “luigi”: a single swapped character (a **typosquat**) is easy to miss.",
          "Stickers on tables, parking meters and posters are trivial to replace with a malicious one.",
        ],
        protect: [
          "Before tapping Open, read the full domain on the preview — letter by letter.",
          "Prefer a paper menu or type the restaurant's known web address yourself.",
          "Treat a QR code like a link from a stranger: if anything looks off, don't open it.",
        ],
        remember: "A QR code is a link you can't read. Check the address before you open it.",
      },
    },

    {
      id: "b_perms",
      type: "device",
      deviceId: "phone",
      guide: {
        title: "The page wants access",
        body: "Before showing the menu, the page asks for camera, microphone and location. Decide whether to allow them.",
      },
      safeChoice: { label: "Don't allow: close the page", eventId: "denied_permissions" },
      completeOn: { action: "grant_permissions" },
    },
    {
      id: "b_teach_perms_safe",
      type: "teach",
      title: "A menu doesn't need all that",
      teach: {
        outcome: "safe",
        body:
          "To show a list of food, a website needs nothing. Yet it demanded your **camera**, **microphone** and **precise location**, and made the menu “unavailable” until you agreed. Closing the page was right: once granted, it could quietly photograph you, record audio and pin you to a place and time.",
        spot: [
          "The reasons given (“scan loyalty codes”, “voice ordering”, “confirm your table”) are flimsy excuses.",
          "Being blocked until you “Allow” is a manipulation, not a real requirement.",
          "A simple menu asking for camera + mic + location is wildly disproportionate.",
        ],
        protect: [
          "Say no to permission prompts that don't match what you're doing.",
          "On iPhone/Android you can revoke camera, mic and location per-site in Settings.",
          "If a page won't work without invasive access, leave: that's a red flag, not a bug.",
        ],
        remember: "If the ask doesn't fit the task, deny it.",
        continueLabel: "See what would have happened",
        whatIf: "If you had allowed access, this is what the page would have captured.",
      },
    },
    {
      id: "b_teach_perms",
      type: "teach",
      title: "A menu doesn't need all that",
      teach: {
        body:
          "To show a list of food, a website needs… nothing. Yet it demanded your **camera**, **microphone** and **precise location**, and made the menu “unavailable” until you agreed. That coercion is the whole point — once granted, the page can quietly photograph you, record audio and pin you to a place and time.",
        spot: [
          "The reasons given (“scan loyalty codes”, “voice ordering”, “confirm your table”) are flimsy excuses.",
          "Being blocked until you “Allow” is a manipulation, not a real requirement.",
          "A simple menu asking for camera + mic + location is wildly disproportionate.",
        ],
        protect: [
          "Say no to permission prompts that don't match what you're doing.",
          "On iPhone/Android you can revoke camera, mic and location per-site in Settings.",
          "If a page won't work without invasive access, leave — that's a red flag, not a bug.",
        ],
        remember: "If the ask doesn't fit the task, deny it.",
      },
    },

    {
      id: "b_capture",
      type: "device",
      deviceId: "phone",
      guide: {
        title: "Browse the menu",
        body: "Scroll the menu and chat with Giovanni as you would. Keep an eye on the panel on the right.",
      },
      forced: true,
      completeOn: { action: "browse_menu" },
    },
    {
      id: "b_teach_capture",
      type: "teach",
      whatIfNote:
        "If you had allowed access, the page would have taken photos with the front camera (Giovanni was in the shot), recorded your conversation through the microphone, and logged your exact location: Ristorante Luigi, Via Roma 14, at 12:48.",
      title: "They got everything they needed",
      teach: {
        body:
          "While you read the menu, the page used its camera, microphone and location access to grab **photos from the front camera**, **record your conversation** (your voice and Giovanni's), and log **where and when** you were. None of it looked like an attack — it looked like lunch. That stolen face + voice is all a modern deepfake needs.",
        spot: [
          "Nothing on screen warned you it was capturing — the menu looked normal.",
          "A few seconds of clear voice and a face photo are enough to clone someone.",
          "Camera, mic and location together reveal who you are, who you're with and where.",
        ],
        protect: [
          "Assume any site you granted camera/mic to can record without an obvious sign.",
          "Cover or revoke access the moment you don't need it.",
          "Be wary of QR “menus”, “surveys” and “free Wi‑Fi” pages that want device access.",
        ],
        remember: "Your face and voice are credentials too. Don't hand them to a sticker.",
      },
    },

    {
      id: "b_to_hacker",
      type: "camera",
      waypointId: "wp_hacker_establish",
      environmentId: "hacker",
    },
    {
      id: "b_hacker_title",
      type: "title",
      kicker: "The other side",
      title: "Meanwhile, on the attacker's laptop",
      guide: {
        title: "The attacker's view",
        body: "This is the fictional criminal who put the fake menu sticker on the table. He wants photos and a voice recording of Maria's family, to fake a call that asks her for money. Here is what he could see after your choice.",
      },
      autoAdvanceMs: 11000,
    },
    { id: "b_hacker_push", type: "camera", waypointId: "wp_hacker_laptop" },
    {
      id: "b_studio",
      type: "device",
      deviceId: "studio",
      guide: {
        title: "The attacker's view",
        body: "You are watching the attacker's screen. He feeds the stolen photo and the voice recording into a deepfake tool.",
      },
      whatIfNote: "If you had allowed access, this is what the attacker would build from your lunch.",
      completeOn: { action: "continue" },
    },
    {
      id: "b_teach_deepfake",
      type: "teach",
      whatIfNote: "If you had allowed access, one stolen photo and that voice recording would be enough for this deepfake.",
      title: "Your son, faked",
      teach: {
        body:
          "With one clear photo and a short voice sample, off-the-shelf tools produce a **deepfake**: a video that looks and sounds like Giovanni and can say anything the attacker types. The technology is cheap, fast and frighteningly convincing — and now it's pointed at the people who love you.",
        spot: [
          "Deepfakes no longer need hours of footage — seconds of audio + a photo can be enough.",
          "Attackers pair it with urgency and emotion so you react before you think.",
          "“I can hear it's really them” is exactly the instinct the attack exploits.",
        ],
        protect: [
          "Agree a family **safe word** for real emergencies — something a deepfake won't know.",
          "Always verify shocking requests on a **known number**, in a separate call.",
          "Never move money on the strength of a single call, however real it sounds.",
        ],
        remember: "Seeing and hearing is no longer believing. Verify on a channel you trust.",
      },
    },

    {
      id: "b_days_later",
      type: "title",
      title: "A few days later…",
      guide: { title: "A few days later", body: "Back home. An ordinary evening." },
      autoAdvanceMs: 3400,
    },
    {
      id: "b_to_living",
      type: "camera",
      waypointId: "wp_living_establish",
      environmentId: "living",
    },
    { id: "b_living_push", type: "camera", waypointId: "wp_living_phone" },
    {
      id: "b_call",
      type: "device",
      deviceId: "soncall",
      guide: {
        title: "Your phone is ringing",
        body: "It's Giovanni — on video. Listen to what he's asking for, and decide carefully.",
      },
    },

    {
      id: "b_teach_fail",
      type: "teach",
      title: "That wasn't Giovanni",
      teach: {
        body:
          "The money is gone, and the real Giovanni is fine — he never called. The face and voice on that call were the **deepfake** built from what the menu QR stole at lunch. Emergency + a loved one's voice + a deadline is the formula: it's engineered to make you act before you can verify.",
        spot: [
          "Sudden crisis, an account number ready, and pressure to send “right now”.",
          "“My phone's about to die / the officer is waiting” shuts down your questions.",
          "Nothing on the call proved it was him: a familiar face and voice can be faked.",
        ],
        protect: [
          "Stop. Hang up and call the person back on their **own known number**.",
          "Use your family **safe word**, or ask a question only they could answer.",
          "No legitimate emergency requires an instant, unverifiable bank transfer.",
        ],
        remember: "Hang up and call back on a number you already trust — every time.",
      },
    },
    {
      id: "b_teach_win",
      type: "teach",
      title: "You stopped the scam",
      teach: {
        body:
          "You refused to be rushed and verified independently — exactly right. The call was a **deepfake** of Giovanni, built from the face and voice the menu QR harvested at lunch. By hanging up and reaching the real Giovanni on his known number, you proved it was fake before any money moved.",
        spot: [
          "Urgency + emotion + an account ready to receive is the deepfake-scam signature.",
          "A real loved one will understand you checking — a scammer will fight it.",
          "Calling back on his own number settled it without arguing with the caller.",
        ],
        protect: [
          "Always verify shocking money requests on a separate, known channel.",
          "Agree a family safe word now, before you ever need it.",
          "Tell others — these calls work because people don't expect them.",
        ],
        remember: "When in doubt, hang up and call back. It cost you a minute and saved €3,200.",
      },
    },
    {
      id: "b_teach_ask_win",
      type: "teach",
      title: "Your question exposed him",
      teach: {
        outcome: "safe",
        body:
          "You asked something only the real Giovanni would know, and he dodged it. Then you hung up and called him back on his own number. That is the full move: the question raised the doubt, the call-back proved it. The call was a **deepfake** built from what the menu QR harvested at lunch.",
        spot: [
          "He couldn't answer a question only the real Giovanni would know, and changed the subject.",
          "Urgency + emotion + an account ready to receive is the deepfake-scam signature.",
          "A dodged question followed by more pressure is a scammer, not a son in trouble.",
        ],
        protect: [
          "Ask something only the real person knows, then verify on their own known number.",
          "Agree a family safe word now, before you ever need it.",
          "Tell others: these calls work because people don't expect them.",
        ],
        remember: "A question raises the doubt; calling back on a number you trust settles it.",
      },
    },
    {
      id: "b_teach_ask_fail",
      type: "teach",
      title: "He dodged, and you still paid",
      teach: {
        outcome: "risky",
        body:
          "Your instinct was right: you asked something only Giovanni would know, and he couldn't answer. But the pressure won and the money went. The real Giovanni is fine and never called. A dodged question is the moment to hang up, not to give in.",
        spot: [
          "He couldn't answer your question and pushed harder instead.",
          "Sudden crisis, an account number ready, and pressure to send right now.",
          "“My phone's about to die / the officer is waiting” is there to stop you thinking.",
        ],
        protect: [
          "If the caller dodges a personal question, end the call.",
          "Call back on the person's own known number before any money moves.",
          "No real emergency needs an instant, unverifiable bank transfer.",
        ],
        remember: "If they can't answer, don't pay. Hang up and call back.",
      },
    },

    {
      id: "b_next_morning",
      type: "title",
      title: "The next morning, at work",
      guide: { title: "The next morning", body: "Before the quiz, one short film on your work computer." },
      autoAdvanceMs: 3400,
    },
    { id: "b_to_office", type: "camera", waypointId: "wp_office_establish", environmentId: "office" },
    { id: "b_office_push", type: "camera", waypointId: "wp_office_monitor" },
    {
      id: "b_desktop_video",
      type: "device",
      deviceId: "desktop",
      title: "At your desk",
      guide: {
        title: "At your desk",
        body: "Click Browser. The page explains quishing and has a short film.",
        actionLabel: "Browser",
      },
      completeOn: { action: "open_app", arg: "browser" },
    },
    {
      id: "b_se_video",
      type: "device",
      deviceId: "sebrief",
      title: "Watch the film",
      guide: {
        title: "Watch the film",
        body: "Click Watch the video.",
        actionLabel: "Watch the video",
      },
      completeOn: { action: "video_done" },
    },

    {
      id: "b_quiz",
      type: "quiz",
      title: "Check your understanding",
      quiz: [
        {
          id: "q1",
          prompt: "You scan a table QR and your phone offers to open “restaurant-lu1gi.com”. What's the safest move?",
          options: [
            { id: "a", label: "Open it — it came from the restaurant's table" },
            { id: "b", label: "Read the full address first; “lu1gi” isn't “luigi”, so don't open it", correct: true },
            { id: "c", label: "Open it but don't enter any passwords" },
          ],
          rationale:
            "A QR hides the URL until you scan it. A swapped character (1 for l) is a typosquat — verify the domain before opening.",
        },
        {
          id: "q2",
          prompt: "A “digital menu” page won't load until you grant camera, microphone and location. You should…",
          options: [
            { id: "a", label: "Allow them all so you can see the menu" },
            { id: "b", label: "Refuse — a menu needs none of that; leave the page", correct: true },
            { id: "c", label: "Allow location only, that one's harmless" },
          ],
          rationale:
            "Permissions should match the task. A menu needs no camera, mic or location. Being blocked until you allow is coercion.",
        },
        {
          id: "q3",
          prompt: "Days later “your son” video-calls in a panic needing money wired immediately. Best response?",
          options: [
            { id: "a", label: "Send it — you can see and hear it's him" },
            { id: "b", label: "Hang up and call him back on his own known number to verify", correct: true },
            { id: "c", label: "Send half now and the rest after he explains" },
          ],
          rationale:
            "Deepfakes can fake a face and voice from seconds of stolen data. Verify on a known channel before any money moves.",
        },
      ],
    },
  ],

  edges: [
    { from: "b_intro", to: "b_to_table" },
    { from: "b_to_table", to: "b_scan" },
    { from: "b_scan", to: "b_teach_scan_safe", on: "safe" },
    { from: "b_scan", to: "b_teach_typo" },
    { from: "b_teach_typo", to: "b_perms" },
    { from: "b_teach_scan_safe", to: "b_perms" },
    { from: "b_perms", to: "b_teach_perms_safe", on: "safe" },
    { from: "b_perms", to: "b_teach_perms" },
    { from: "b_teach_perms", to: "b_capture" },
    { from: "b_teach_perms_safe", to: "b_capture" },
    { from: "b_capture", to: "b_teach_capture" },
    { from: "b_teach_capture", to: "b_to_hacker" },
    { from: "b_to_hacker", to: "b_hacker_title" },
    { from: "b_hacker_title", to: "b_hacker_push" },
    { from: "b_hacker_push", to: "b_studio" },
    { from: "b_studio", to: "b_teach_deepfake" },
    { from: "b_teach_deepfake", to: "b_days_later" },
    { from: "b_days_later", to: "b_to_living" },
    { from: "b_to_living", to: "b_living_push" },
    { from: "b_living_push", to: "b_call" },
    { from: "b_call", to: "b_teach_fail", on: "send" },
    { from: "b_call", to: "b_teach_win", on: "verify" },
    { from: "b_call", to: "b_teach_ask_fail", on: "send:asked" },
    { from: "b_call", to: "b_teach_ask_win", on: "verify:asked" },
    { from: "b_call", to: "b_teach_win" },
    { from: "b_teach_fail", to: "b_next_morning" },
    { from: "b_teach_win", to: "b_next_morning" },
    { from: "b_teach_ask_fail", to: "b_next_morning" },
    { from: "b_teach_ask_win", to: "b_next_morning" },
    { from: "b_next_morning", to: "b_to_office" },
    { from: "b_to_office", to: "b_office_push" },
    { from: "b_office_push", to: "b_desktop_video" },
    { from: "b_desktop_video", to: "b_se_video" },
    { from: "b_se_video", to: "b_quiz" },
  ],

  initialBeatId: "b_intro",
  passThreshold: 70,
};
